import { Box } from "@mui/material";
import { Signal, useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import { has, partition } from "lodash-es";
// import { useEffect } from "react"; // Removed useEffect as it's no longer used after removing overlay logic
import { getObjectDataDebounced, getScope, getState, GetTheme, setupLocalState } from "../../library/dataService";
import { getExprValue } from "../../library/interpreter";
import { log } from "../../library/logger";
import { getTypename, ItemNode } from "../../library/metadataUtils";
import { isNullOrEmpty } from "../../library/utils";
import { InspectorJsonView } from "../NGJsonView/NGJsonView";

const tag = "NGStateInspector";
const MAX_ARRAY_SIZE = 100;

export function NGStateInspector({ config, context }) {
  const { global, state, form, parentState } = getState(context);

  const local = setupLocalState(
    {
      ...config,
      Bindings: {
        PropertiesEditorData: "State.PropertiesEditorData",
      },
    },
    {
      PropertiesEditorData: useSignal({}),
    },
    context
  );

  const selectedItemSignal: Signal<{ node: ItemNode; element: Element } | null> = useSignal(null);

  useSignalEffect(() => {
    const selectedNode = local.PropertiesEditorData.value;
    let selectedNodeId = selectedNode?.Id;

    const type = getTypename(selectedNode);
    if (type === "Page") {
      selectedNodeId = "page-editor";
    }

    if (!selectedNodeId) {
      selectedItemSignal.value = null;
      log.debug(tag, "No ID selected");
      return;
    }

    const iframe = document.getElementById("EditorIFrame") as HTMLIFrameElement | null;
    if (!iframe) {
      selectedItemSignal.value = null;
      log.debug(tag, "Iframe not found");
      return;
    }

    let innerDoc: Document | null = null;
    if (iframe.contentWindow && iframe.contentWindow.document) {
      innerDoc = iframe.contentWindow.document;
    } else if (iframe.contentDocument) {
      innerDoc = iframe.contentDocument;
    }

    if (!innerDoc) {
      selectedItemSignal.value = null;
      log.debug(
        tag,
        "Could not access iframe document. This might be due to cross-origin restrictions or the iframe not being fully loaded."
      );
      return;
    }

    // Find the element within the iframe's document
    const element = innerDoc.querySelector(`[data-testid="${selectedNodeId}"]`);
    if (!element) {
      selectedItemSignal.value = null;
      log.debug(tag, 'Element with data-testid="' + selectedNodeId + '" not found inside the iframe.');
      return;
    }

    selectedItemSignal.value = { node: selectedNode, element };
  });

  const search = useSignal("");
  const expression = useSignal("");

  function toStateInfo(node: ItemNode, element: Element | null) {
    const { Id, config: nodeConfig } = node;
    const type = getTypename(node);

    const [classesMui, classes] = element?.className
      ? partition(element.className.split(" "), (x) => x.startsWith("Mui") || x.startsWith("css-"))
      : [[], []]; // Handle null element

    const scope = getScope(context, nodeConfig, state, form, null, parentState);
    const scopeData = getObjectDataDebounced(scope, MAX_ARRAY_SIZE);
    const globalData = getObjectDataDebounced(global, MAX_ARRAY_SIZE);
    const scopeSubsetData = getObjectDataDebounced(
      {
        Row: scope.Row,
        RowIndex: scope.RowIndex,
        Form: scope.Form,
        FormData: scope.FormData,
        Repeater: scope.Repeater,
        Grid: scope.Grid,
      },
      MAX_ARRAY_SIZE
    );
    const stateData = getObjectDataDebounced(state, MAX_ARRAY_SIZE);

    return {
      Id,
      Type: type,
      Global: globalData,
      State: stateData,
      Scope: scopeData,
      Details: {
        Id,
        Type: type,
        Classes: classes,
        "Classes MUI": classesMui,
        Global: globalData,
        State: stateData,
        Scope: scopeSubsetData,
      },
    };
  }

  const info = useComputed(() => {
    if (selectedItemSignal.value && selectedItemSignal.value.node) {
      return toStateInfo(selectedItemSignal.value.node, selectedItemSignal.value.element);
    }
    return null;
  });

  const filteredInfoDetails = useComputed(() => {
    const details = info.value?.Details;
    if (!details || !search.value) return details;

    const searchLower = search.value.toLowerCase();

    const filterObject = (obj) => {
      if (typeof obj !== "object" || obj === null) return null;

      const filtered = Object.entries(obj).reduce((acc, [key, value]) => {
        if (key.toLowerCase().includes(searchLower) || String(value).toLowerCase().includes(searchLower)) {
          acc[key] = value;
        } else if (typeof value === "object") {
          const nested = filterObject(value);
          if (nested && Object.keys(nested).length > 0) {
            acc[key] = nested;
          }
        }
        return acc;
      }, {});

      return Object.keys(filtered).length > 0 ? filtered : null;
    };

    return filterObject(info.value);
  });

  const expressionResult = useComputed(() => {
    const currentExpression = expression.value;
    if (isNullOrEmpty(currentExpression)) return { Result: "Provide an expression" };
    if (currentExpression.trim().endsWith(".")) return { Result: "Incomplete expression" };
    if (!selectedItemSignal.value) return { Result: "Select a component" };
    const scope = info.value?.Scope ?? {};
    const error = {};
    const result = getExprValue(currentExpression, scope, null, false, { merge: false, init: false }, error);
    return { Result: has(error, "message") ? "Invalid expression" : result };
  });

  const expressionResultData = getObjectDataDebounced(expressionResult.value, MAX_ARRAY_SIZE);

  const showDetails = useSignal(true);
  const showEvaluate = useSignal(true);

  const zIndex = 1200;

  const { ThemeMode: themeMode } = GetTheme();
  const theme = themeMode === "dark" ? "monokai" : "rjv-default";
  const inspectorBackground = themeMode === "dark" ? "#333" : "#fff";

  return (
    <Box
      className="inspector-info"
      sx={{
        background: inspectorBackground,
        border: "1px solid #a59f85",
        color: "#fff",
        display: "flex",
        flex: 1,
        flexDirection: "column",
        fontSize: "12px",
        overflow: "hidden",
        position: "relative",
        zIndex: zIndex + 1,
        "& .inspector-panel": {
          borderBottom: "1px solid #a59f85",
        },
        "& .inspector-panel:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <InspectorJsonView
        collapsed={1}
        src={
          filteredInfoDetails.value ?? { [search.value || "Key"]: search.value ? "Not found" : "Select a component" }
        }
        name={info.value?.Type ?? (filteredInfoDetails.value ? "Unknown Type" : "Component")}
        displayDataTypes={false}
        expanded={showDetails.value}
        quotesOnKeys={false}
        onChangeSearch={(value) => (search.value = value)}
        onToggleExpand={() => (showDetails.value = !showDetails.value)}
        placeholder="Search"
        search={search.value}
        theme={theme}
        title="Details"
      />

      <InspectorJsonView
        collapsed={2}
        displayDataTypes={false}
        expanded={showEvaluate.value}
        name="Expression"
        onChangeSearch={(value) => (expression.value = value)}
        onToggleExpand={() => (showEvaluate.value = !showEvaluate.value)}
        placeholder="Expression. E.g. Row.Name, map(State.Quote, 'Price')"
        quotesOnKeys={false}
        search={expression.value}
        src={expressionResultData}
        theme={theme}
        title="Evaluate"
      />
    </Box>
  );
}
